import type { AppInfo } from 'get-installed-apps-on-mac';

export type Result<T, E = Error> =
  | { status: 'success'; data: T }
  | { status: 'error'; reason: E };

/**
 * Domain category identifiers
 * These must match the Swift DomainCategoryID enum values
 * Defined as constants first to serve as single source of truth
 */
export const CATEGORY_IDS = {
  BANKING_FINANCE: 'banking-finance',
  HEALTH: 'health',
  SOCIAL: 'social',
  SHOPPING: 'shopping',
  ENTERTAINMENT: 'entertainment',
  ADULT_CONTENT: 'adult-content',
} as const;

/**
 * Category ID type derived from constants
 */
export type CategoryID = (typeof CATEGORY_IDS)[keyof typeof CATEGORY_IDS];

/**
 * Default enabled categories
 * These are enabled on first launch but can be toggled by the user
 */
export const DEFAULT_ENABLED_CATEGORY_IDS: readonly CategoryID[] = [
  CATEGORY_IDS.ADULT_CONTENT,
  CATEGORY_IDS.BANKING_FINANCE,
] as const;

/**
 * Helper to validate if a string is a valid CategoryID
 */
export function isCategoryID(value: string): value is CategoryID {
  return Object.values(CATEGORY_IDS).includes(value as CategoryID);
}

/**
 * WebSocket event the backend pushes when a domain the user browsed first
 * becomes a client-visible auto-classification. The string literal MUST stay
 * identical to the backend `DOMAIN_CLASSIFIED_WS_EVENT` constant.
 */
export const DOMAIN_CLASSIFIED_WS_EVENT = 'domain_classified';

export const TEAM_EXCLUSIONS_CHANGED_WS_EVENT = 'team_exclusions_changed';

export interface WebSocketConnectionResult {
  success: boolean;
  error?: string;
  state?: 'connecting' | 'ready' | 'disconnected';
}

export interface DomainClassifiedNotification {
  domain: string;
  categoryId: CategoryID;
  categoryName: string;
}

export interface InstalledApp {
  name: string;
  path: string;
  icon?: string | null;
  displayName: string;
  identifier: string;
}

export type AppAccessMode = 'block' | 'allow';

export interface AppAccessPolicy {
  appAccessMode: AppAccessMode;
  excludedBundleIds: string[];
  allowedBundleIds: string[];
  appAccessModeLockedByTeam?: boolean;
}

export interface AppAccessPolicyWriteResult {
  success: boolean;
  error?: string;
  policy?: AppAccessPolicy;
}

export type AppPickerErrorCode =
  | 'no_app_selected'
  | 'installed_apps_unavailable'
  | 'unreadable_app';

export interface AppPickerResult {
  success: boolean;
  code?: AppPickerErrorCode;
  error?: string;
  data?: AppWithExclusionState[];
}

export interface AppExclusionSettings {
  excludedBundleIds: string[];
  appAccessMode?: AppAccessMode;
  allowedBundleIds?: string[];
  lastUpdated: number;
  customExcludedApps: AppInfo[];
  excludedDomains: string[];
  userIncludedDomains?: string[];
  enabledCategoryIds?: CategoryID[];
  teamExcludedBundleIds?: string[];
  teamExcludedDomains?: string[];
  teamAllowedBundleIds?: string[] | null;
}

/**
 * Category definition
 * Does not include enabled state - TypeScript manages that via ExclusionManager
 */
export interface CategoryInfo {
  id: CategoryID;
  name: string;
  description: string;
}

/**
 * Static category metadata — single source of truth for display names and descriptions.
 * Sorted by display order (most sensitive first).
 */
export const CATEGORY_INFO: readonly CategoryInfo[] = [
  {
    id: CATEGORY_IDS.ADULT_CONTENT,
    name: 'Adult Content',
    description: 'Adult websites and mature content',
  },
  {
    id: CATEGORY_IDS.BANKING_FINANCE,
    name: 'Banking & Finance',
    description:
      'Bank accounts, financial transactions, and investment platforms',
  },
  {
    id: CATEGORY_IDS.HEALTH,
    name: 'Health & Medical',
    description:
      'Medical records, health information, and healthcare providers',
  },
  {
    id: CATEGORY_IDS.SOCIAL,
    name: 'Social Media',
    description: 'Social media platforms and messaging services',
  },
  {
    id: CATEGORY_IDS.SHOPPING,
    name: 'Shopping',
    description: 'E-commerce sites and online shopping platforms',
  },
  {
    id: CATEGORY_IDS.ENTERTAINMENT,
    name: 'Entertainment',
    description: 'Streaming services, games, and entertainment platforms',
  },
] as const;

export interface AppWithExclusionState extends InstalledApp {
  isDefaultExclusion: boolean;
  canUserModify: boolean;
  isExcluded: boolean;
  lockedBy?: 'default' | 'team';
}

export interface DomainWithExclusionState {
  domain: string;
  canUserModify: boolean;
  lockedBy?: 'default' | 'team';
}

export interface LogFileInfo {
  name: string;
  path: string;
  size: number;
  modified: string;
  created: string;
  isCrashLog?: boolean;
}

export enum ContextCollectionState {
  LOADING = 'loading',
  RUNNING = 'running',
  STOPPED = 'stopped',
  PAUSED = 'paused',
  STOPPING = 'stopping',
  // Distinct from STOPPED (which auto-starts on a fresh launch) so the renderer's
  // auto-start guard can skip it. Persisted, so it survives a relaunch
  DISABLED = 'disabled',
  ERROR = 'error',
  SPAWNING = 'spawning',
}

export type ContextCollectionConsent = 'undecided' | 'granted' | 'declined';

export interface ContextCollectionConsentRecord {
  consent: ContextCollectionConsent;
  updatedAt: string;
}

export interface ContextCollectionTimerState {
  isPaused: boolean;
  pausedAt?: Date;
  pauseDurationMs?: number;
  remainingTimeMs?: number;
  autoResumeAt?: Date;
}

export interface ContextCollectionStatus {
  state: ContextCollectionState;
  isRunning: boolean;
  lastChecked: Date;
  error?: string;
  timerState?: ContextCollectionTimerState;
  consent?: ContextCollectionConsentRecord;
}
